import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Map as MapIcon, Radio, ShieldCheck, Crown, Gift, Users, Sparkles, Target, Info, Flag, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_CODE = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
  "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL",
  "18": "IN", "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD",
  "25": "MA", "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE",
  "32": "NV", "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV",
  "55": "WI", "56": "WY",
};

const SEG_USUARIOS = [
  { key: "total", label: "Total", icon: Users, accent: [212, 175, 55] },
  { key: "premium", label: "Premium", icon: Crown, accent: [255, 215, 0] },
  { key: "trial", label: "Trial", icon: Sparkles, accent: [78, 205, 196] },
  { key: "free", label: "Free", icon: Gift, accent: [161, 161, 170] },
];

const Mapa = () => {
  const [vista, setVista] = useState("usuarios"); // usuarios | juegos
  const [seg, setSeg] = useState("total");
  const [dataU, setDataU] = useState(null);
  const [dataJ, setDataJ] = useState(null);
  const [hover, setHover] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [u, j] = await Promise.all([
        api.get("/comando/mapa/usuarios"),
        api.get("/comando/mapa/juegos"),
      ]);
      setDataU(u.data);
      setDataJ(j.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.title = "Mapas · AXIORA";
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const pendiente = vista === "usuarios" ? dataU?.puente_pendiente : dataJ?.puente_pendiente;

  // --- Usuarios por estado ---
  const byEstadoU = useMemo(() => {
    const map = {};
    (dataU?.estados || []).forEach((e) => { if (e.estado) map[String(e.estado).toUpperCase()] = e; });
    return map;
  }, [dataU]);

  // --- Actividad de juego por estado (excluye MULTI) ---
  const byEstadoJ = useMemo(() => {
    const map = {};
    (dataJ?.por_estado || []).forEach((e) => {
      const code = String(e.estado || "").toUpperCase();
      if (code && code !== "MULTI") map[code] = e;
    });
    return map;
  }, [dataJ]);

  const juegos = useMemo(() => {
    const arr = [...(dataJ?.juegos || [])];
    arr.sort((a, b) => (b.puntaje_uso || 0) - (a.puntaje_uso || 0));
    return arr;
  }, [dataJ]);

  const nacionales = useMemo(() => juegos.filter((j) => String(j.estado || "").toUpperCase() === "MULTI"), [juegos]);
  const maxPuntajeJuego = useMemo(() => juegos.reduce((m, j) => Math.max(m, j.puntaje_uso || 0), 0), [juegos]);
  const candidatos = useMemo(() => {
    const conActividad = juegos.filter((j) => (j.puntaje_uso || 0) >= 0);
    return new Set(conActividad.slice(-5).map((j) => j.game_id));
  }, [juegos]);

  // --- Selección de valor para el mapa según la vista ---
  const accent = vista === "usuarios" ? (SEG_USUARIOS.find((s) => s.key === seg)?.accent || [212, 175, 55]) : [212, 175, 55];
  const valorMapa = useCallback((code) => {
    if (vista === "usuarios") return byEstadoU[code]?.[seg] || 0;
    return byEstadoJ[code]?.puntaje_uso || 0;
  }, [vista, seg, byEstadoU, byEstadoJ]);

  const maxMapa = useMemo(() => {
    let m = 0;
    Object.keys(FIPS_TO_CODE).forEach((f) => { m = Math.max(m, valorMapa(FIPS_TO_CODE[f])); });
    return m;
  }, [valorMapa]);

  const fillFor = (code) => {
    const c = valorMapa(code);
    if (!c || !maxMapa) return "#0f0f12";
    const t = Math.max(0.14, c / maxMapa);
    const [r, g, b] = accent;
    return `rgba(${r},${g},${b},${(0.14 + 0.86 * t).toFixed(3)})`;
  };

  const ranking = useMemo(() => {
    if (vista === "usuarios") {
      return Object.values(byEstadoU)
        .map((e) => ({ code: String(e.estado).toUpperCase(), n: e[seg] || 0 }))
        .filter((r) => r.n > 0).sort((a, b) => b.n - a.n).slice(0, 10);
    }
    return Object.values(byEstadoJ)
      .map((e) => ({ code: String(e.estado).toUpperCase(), n: e.puntaje_uso || 0 }))
      .filter((r) => r.n > 0).sort((a, b) => b.n - a.n).slice(0, 10);
  }, [vista, seg, byEstadoU, byEstadoJ]);

  return (
    <div className="p-6" data-testid="mapa-page">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide flex items-center gap-3">
            <MapIcon className="w-7 h-7 text-[#D4AF37]" /> Mapas
          </h2>
          <p className="text-xs text-[#71717A] mt-0.5">Concentración de usuarios y actividad de juego por estado</p>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] border border-[#1F1F24] p-1">
          <button data-testid="mapa-toggle-usuarios" onClick={() => setVista("usuarios")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider transition-all ${vista === "usuarios" ? "bg-[#D4AF37]/15 text-[#FFD700]" : "text-[#A1A1AA] hover:text-[#F5F5F7]"}`}>
            <Users className="w-3.5 h-3.5" /> Usuarios
          </button>
          <button data-testid="mapa-toggle-juegos" onClick={() => setVista("juegos")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider transition-all ${vista === "juegos" ? "bg-[#D4AF37]/15 text-[#FFD700]" : "text-[#A1A1AA] hover:text-[#F5F5F7]"}`}>
            <Target className="w-3.5 h-3.5" /> Actividad de juego
          </button>
        </div>
      </header>

      {/* Sub-controles */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        {vista === "usuarios" ? (
          <div className="flex items-center gap-2">
            {SEG_USUARIOS.map((s) => {
              const Icon = s.icon; const active = seg === s.key;
              return (
                <button key={s.key} data-testid={`mapa-seg-${s.key}`} onClick={() => setSeg(s.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider border transition-all ${active ? "border-[#D4AF37]/50 text-[#FFD700] bg-[#D4AF37]/10" : "border-[#1F1F24] text-[#A1A1AA] hover:text-[#F5F5F7]"}`}>
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[11px] text-[#71717A]" title={dataJ?.formula || ""}>
            <Info className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="font-mono-ax">{dataJ?.formula || "puntaje_uso = boletos×3 + combos_guardadas×2 + favoritos×1"}</span>
          </div>
        )}
      </div>

      {pendiente && (
        <div data-testid="mapa-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — sin datos del puente por ahora.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Mapa */}
        <div className="lg:col-span-2 rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-3">
          <ComposableMap projection="geoAlbersUsa" width={975} height={610} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const code = FIPS_TO_CODE[geo.id] || "";
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHover({ code, name: geo.properties.name })}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        default: { fill: fillFor(code), stroke: "#1F1F24", strokeWidth: 0.5, outline: "none" },
                        hover: { fill: `rgba(${accent[0]},${accent[1]},${accent[2]},0.95)`, stroke: "#D4AF37", strokeWidth: 1, outline: "none", cursor: "pointer" },
                        pressed: { fill: "#D4AF37", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          <div className="flex items-center justify-between px-2 pt-2 text-[10px] text-[#71717A]">
            <span>Menos</span>
            <div className="h-2 flex-1 mx-3 rounded-full" style={{ background: `linear-gradient(90deg, #0f0f12, rgba(${accent[0]},${accent[1]},${accent[2]},1))` }} />
            <span>Más {maxMapa ? `(máx. ${fmtNum(maxMapa)})` : ""}</span>
          </div>
          <p className="px-2 pt-2 text-[10px] text-[#52525b] leading-relaxed">
            {vista === "usuarios"
              ? (dataU?.metodo || "La ubicación se infiere por los JUEGOS ESTATALES que usa cada usuario (boletos, favoritos, combinaciones) — NUNCA por teléfono, GPS ni datos personales. Solo conteos agregados. Conforme a Apple/Google.")
              : "Coloreado por actividad de juego agregada por estado. Los juegos nacionales (MULTI) no se ubican en el mapa; ver tarjeta aparte."}
          </p>
        </div>

        {/* Panel lateral */}
        <div className="space-y-4">
          <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-4 min-h-[92px]">
            <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-2">Estado seleccionado</p>
            {hover ? (
              <div data-testid="mapa-hover">
                <p className="font-serif-ax text-xl text-[#FFD700]">{hover.name}{hover.code ? ` · ${hover.code}` : ""}</p>
                {vista === "usuarios" ? (
                  <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                    <MiniStat label="Prem." value={byEstadoU[hover.code]?.premium || 0} color="#FFD700" />
                    <MiniStat label="Trial" value={byEstadoU[hover.code]?.trial || 0} color="#4ECDC4" />
                    <MiniStat label="Free" value={byEstadoU[hover.code]?.free || 0} color="#A1A1AA" />
                    <MiniStat label="Total" value={byEstadoU[hover.code]?.total || 0} color="#F5F5F7" />
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                    <MiniStat label="Boletos" value={byEstadoJ[hover.code]?.boletos || 0} color="#FFD700" />
                    <MiniStat label="Combos" value={byEstadoJ[hover.code]?.combos_guardadas || 0} color="#4ECDC4" />
                    <MiniStat label="Favs" value={byEstadoJ[hover.code]?.favoritos || 0} color="#A1A1AA" />
                    <MiniStat label="Puntaje" value={byEstadoJ[hover.code]?.puntaje_uso || 0} color="#F5F5F7" />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[#52525b] text-sm">Pasa el cursor sobre un estado para ver su detalle.</p>
            )}
          </div>

          {/* Contador sin estado (usuarios) o Juegos Nacionales (juegos) */}
          {vista === "usuarios" ? (
            <div data-testid="mapa-sin-estado" className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-4">
              <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Sin ubicación inferible</p>
              <p className="font-mono-ax text-2xl text-[#A1A1AA]">{fmtNum(dataU?.sin_estado ?? 0)}</p>
              <p className="text-[10px] text-[#52525b] mt-1">Usuarios que solo usan juegos nacionales (Powerball, etc.) o aún sin actividad.</p>
              {dataU?.total_ubicados != null && (
                <p className="text-[10px] text-[#52525b] mt-1">Ubicados: <span className="font-mono-ax text-[#A1A1AA]">{fmtNum(dataU.total_ubicados)}</span></p>
              )}
            </div>
          ) : (
            <div data-testid="mapa-juegos-nacionales" className="rounded-xl bg-[#0A0A0A] border border-[#D4AF37]/25 p-4">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Juegos Nacionales (MULTI)</p>
              {nacionales.length ? (
                <div className="space-y-2">
                  {nacionales.map((j) => (
                    <div key={j.game_id} className="flex items-center justify-between text-xs" title={`Boletos ${j.boletos} · Combos ${j.combos_guardadas} · Favs ${j.favoritos}`}>
                      <span className="text-[#F5F5F7]">{j.nombre}</span>
                      <span className="font-mono-ax text-[#FFD700]">{fmtNum(j.puntaje_uso)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#52525b] text-xs">Sin datos.</p>}
            </div>
          )}

          {/* Top estados */}
          <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#1F1F24] text-sm text-[#F5F5F7] uppercase tracking-wider">
              Top estados{vista === "usuarios" ? ` · ${SEG_USUARIOS.find((s) => s.key === seg)?.label}` : " · actividad"}
            </div>
            <div className="divide-y divide-[#1F1F24]">
              {ranking.length ? ranking.map((r, i) => (
                <div key={r.code} data-testid={`mapa-rank-${r.code}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2"><span className="w-5 text-[#52525b] font-mono-ax text-xs">{i + 1}</span><span className="text-[#F5F5F7]">{r.code}</span></span>
                  <span className="font-mono-ax text-[#FFD700]">{fmtNum(r.n)}</span>
                </div>
              )) : <div className="px-4 py-8 text-center text-[#52525b] text-sm">Sin datos todavía.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap de juegos (solo en vista de actividad) */}
      {vista === "juegos" && (
        <div className="mt-4 rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5" data-testid="heatmap-juegos">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider flex items-center gap-2"><Target className="w-4 h-4 text-[#D4AF37]" /> Uso por juego (heat map)</h3>
            <span className="text-[10px] text-[#71717A] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-[#FF6B5B]" /> Últimos del ranking = candidatos a revisar</span>
          </div>
          <div className="space-y-1.5">
            {juegos.length ? juegos.map((j) => {
              const t = maxPuntajeJuego ? Math.max(0.03, (j.puntaje_uso || 0) / maxPuntajeJuego) : 0.03;
              const esCandidato = candidatos.has(j.game_id) && (j.puntaje_uso || 0) < maxPuntajeJuego * 0.15;
              return (
                <div key={j.game_id} data-testid={`juego-bar-${j.game_id}`} className="flex items-center gap-3"
                  title={`Boletos ${j.boletos} · Combos ${j.combos_guardadas} · Favoritos ${j.favoritos} · Puntaje ${j.puntaje_uso}`}>
                  <span className={`w-40 shrink-0 text-xs truncate ${esCandidato ? "text-[#FF6B5B]" : "text-[#F5F5F7]"}`}>
                    {j.nombre}{String(j.estado).toUpperCase() === "MULTI" ? " ·N" : ""}
                  </span>
                  <div className="flex-1 h-5 rounded-md bg-[#0f0f12] overflow-hidden border border-[#1F1F24]">
                    <div className="h-full rounded-md transition-all" style={{
                      width: `${(t * 100).toFixed(1)}%`,
                      background: esCandidato
                        ? "linear-gradient(90deg, rgba(255,107,91,0.35), rgba(255,107,91,0.8))"
                        : `linear-gradient(90deg, rgba(161,161,170,0.25), rgba(212,175,55,${(0.35 + 0.65 * t).toFixed(2)}))`,
                    }} />
                  </div>
                  <span className="w-12 text-right font-mono-ax text-xs text-[#FFD700]">{fmtNum(j.puntaje_uso)}</span>
                </div>
              );
            }) : <p className="text-[#52525b] text-sm text-center py-6">Sin datos de juegos.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, color }) => (
  <div className="rounded-lg bg-[#050505] border border-[#1F1F24] py-2">
    <div className="font-mono-ax text-base" style={{ color }}>{fmtNum(value)}</div>
    <div className="text-[9px] text-[#71717A] uppercase tracking-wider">{label}</div>
  </div>
);

export default Mapa;
